import { cloneElement, isValidElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, ReactElement, ReactNode } from 'react';

/**
 * Tooltip (CO-150): a short description of a control or a value, shown on
 * hover, on keyboard focus and on tap, dismissed with Escape. The trigger
 * names it with aria-describedby, so a screen reader announces it with the
 * control rather than as a separate item. It never holds the only copy of
 * something essential: the full value is also on the detail page.
 *
 * `Tooltip` wraps a focusable element (a link, a button); `TooltipText`
 * makes a plain value (a full identity) focusable and tappable so its
 * tooltip is reachable without a mouse. `hoverOnly` is for a value repeated
 * in every row of a list (an exact time): it is not made a tab stop, the
 * bubble shows on hover, and a screen reader gets the same text as the
 * element's description (CO-184, DS7).
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  toggleOnTap = false,
  hoverOnly = false,
}: {
  content: ReactNode;
  children: ReactElement<Record<string, unknown>>;
  side?: 'top' | 'right' | 'bottom';
  /** For a value that is not itself a control: a tap shows it (touch
   * browsers do not focus a button on tap). */
  toggleOnTap?: boolean;
  /** The trigger is not focusable: the bubble shows on hover only and is the element's description. */
  hoverOnly?: boolean;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  // Near the right edge the bubble opens leftwards, so it never widens the page.
  const [alignEnd, setAlignEnd] = useState(false);
  const wrapper = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointer = (event: PointerEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const show = useCallback(() => {
    const box = wrapper.current?.getBoundingClientRect();
    setAlignEnd(Boolean(box && box.left + 320 > window.innerWidth - 16));
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);
  if (!isValidElement(children)) return children;
  const interactive = {
    onFocus: (event: FocusEvent) => {
      show();
      (children.props.onFocus as ((e: FocusEvent) => void) | undefined)?.(event);
    },
    onBlur: (event: FocusEvent) => {
      hide();
      (children.props.onBlur as ((e: FocusEvent) => void) | undefined)?.(event);
    },
    // A tap shows it; a tap elsewhere, Escape or leaving the field hides it.
    ...(toggleOnTap ? { onClick: show } : {}),
  };
  const trigger = cloneElement(children, { 'aria-describedby': id, ...(hoverOnly ? {} : interactive) });
  return (
    <span className="tooltip" ref={wrapper} onMouseEnter={show} onMouseLeave={hide}>
      {trigger}
      <span
        role="tooltip"
        id={id}
        className={`tooltip__bubble tooltip__bubble--${side}${alignEnd && side !== 'right' ? ' tooltip__bubble--end' : ''}`}
        data-open={open ? 'true' : undefined}
      >
        {content}
      </span>
    </span>
  );
}

/** A value whose tooltip says more ("9 min ago" → the exact date and time). */
export function TooltipText({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Tooltip content={label} toggleOnTap>
      <button
        type="button"
        className={`tooltip-text${className ? ` ${className}` : ''}`}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === 'Escape') (event.currentTarget as HTMLElement).blur();
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
}
